import { z } from 'zod';

/**
 * Generates a Pix "Copia e Cola" string (BR Code EMV) per the BACEN spec.
 *
 * Reference: Manual do BR Code (Banco Central do Brasil), based on EMVCo TLV.
 * Each field is encoded as ID (2 digits) + length (2 digits) + value.
 *
 * The output is the canonical static-charge BR Code that mobile banking apps
 * accept both pasted into the "Pix Copia e Cola" field and rendered as a QR.
 *
 * Inputs are normalized: the merchant name and city are stripped of accents,
 * truncated, and uppercased per the spec's ASCII-only requirement. The Pix
 * key is passed through unchanged — callers are expected to validate the key
 * format upstream.
 */

export interface PixBrcodeInput {
  /** Pix key (CPF/CNPJ digits, email, phone in +5511… format, or random key). */
  pixKey: string;
  /** Receiver display name — max 25 chars after normalization. */
  receiverName: string;
  /** Receiver city — max 15 chars after normalization. */
  city: string;
  /** Amount in BRL. Optional — when omitted, the payer types the value. */
  amount?: number;
  /** Free-form description shown in the bank app — max 50 chars. */
  description?: string;
  /** Optional reference id (TXID). Defaults to '***' (no reference). */
  txid?: string;
}

const ID_PAYLOAD_FORMAT = '00';
const ID_MERCHANT_ACCOUNT_INFO = '26';
const ID_MERCHANT_CATEGORY_CODE = '52';
const ID_TRANSACTION_CURRENCY = '53';
const ID_TRANSACTION_AMOUNT = '54';
const ID_COUNTRY_CODE = '58';
const ID_MERCHANT_NAME = '59';
const ID_MERCHANT_CITY = '60';
const ID_ADDITIONAL_DATA = '62';
const ID_CRC16 = '63';

const SUB_PIX_GUI = '00';
const SUB_PIX_KEY = '01';
const SUB_PIX_DESCRIPTION = '02';
const SUB_TXID = '05';

const PIX_GUI = 'BR.GOV.BCB.PIX';

/** Strip diacritics and reject anything outside ASCII per spec. */
const sanitize = (input: string): string =>
  input
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^\x20-\x7E]/g, '')
    .trim();

const tlv = (id: string, value: string): string => {
  const length = value.length.toString().padStart(2, '0');
  return `${id}${length}${value}`;
};

/**
 * CRC-16/CCITT-FALSE (poly 0x1021, init 0xFFFF, no reflect, no xorout).
 * Required by the BR Code spec — calculated over everything up to and
 * including the "6304" prefix of the CRC field itself.
 */
export const crc16 = (input: string): string => {
  let crc = 0xffff;
  for (let i = 0; i < input.length; i++) {
    crc ^= input.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = (crc << 1) ^ 0x1021;
      } else {
        crc = crc << 1;
      }
      crc &= 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
};

const formatAmount = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error('amount must be a positive number');
  }
  return value.toFixed(2);
};

/**
 * Builds the Pix BR Code (Copia e Cola) string. Throws if required fields
 * are empty after sanitization.
 */
export const generatePixBrcode = (input: PixBrcodeInput): string => {
  const pixKey = input.pixKey.trim();
  if (!pixKey) throw new Error('pixKey is required');

  const merchantName = sanitize(input.receiverName).slice(0, 25).toUpperCase();
  if (!merchantName) throw new Error('receiverName is required');

  const merchantCity = sanitize(input.city).slice(0, 15).toUpperCase();
  if (!merchantCity) throw new Error('city is required');

  const description = input.description
    ? sanitize(input.description).slice(0, 50).toUpperCase()
    : '';
  const txid = input.txid ? sanitize(input.txid).slice(0, 25) : '***';

  const merchantAccount = (() => {
    const parts: string[] = [tlv(SUB_PIX_GUI, PIX_GUI), tlv(SUB_PIX_KEY, pixKey)];
    if (description) parts.push(tlv(SUB_PIX_DESCRIPTION, description));
    return tlv(ID_MERCHANT_ACCOUNT_INFO, parts.join(''));
  })();

  const additionalData = tlv(ID_ADDITIONAL_DATA, tlv(SUB_TXID, txid));

  const fields: string[] = [
    tlv(ID_PAYLOAD_FORMAT, '01'),
    merchantAccount,
    tlv(ID_MERCHANT_CATEGORY_CODE, '0000'),
    tlv(ID_TRANSACTION_CURRENCY, '986'),
  ];

  if (input.amount !== undefined) {
    fields.push(tlv(ID_TRANSACTION_AMOUNT, formatAmount(input.amount)));
  }

  fields.push(
    tlv(ID_COUNTRY_CODE, 'BR'),
    tlv(ID_MERCHANT_NAME, merchantName),
    tlv(ID_MERCHANT_CITY, merchantCity),
    additionalData,
  );

  const partial = `${fields.join('')}${ID_CRC16}04`;
  return `${partial}${crc16(partial)}`;
};

export const pixBrcodeInputSchema = z.object({
  pixKey: z.string().min(1).max(140),
  receiverName: z.string().min(1).max(100),
  city: z.string().min(1).max(50),
  amount: z.number().positive().optional(),
  description: z.string().max(100).optional(),
  txid: z.string().max(25).optional(),
});
